import React from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import styled from 'styled-components';
import ControlWithLabel from './ControlWithLabel';
import { PLACEHOLDER_SELECT } from '../../constants/cmConstants';

const DropDown = ({
  value,
  onChange,
  options,
  label,
  isSearchable,
  className,
  isDisabled,
  placeholder,
  allowClear,
  handleSearch,
  isLoading,
  handlePopupScroll,
  isMultiple,
  hasAllOptionsLoaded
}) => {
  // FIN-12644: where the type-to-narrow search happens has to follow the DATA, not a hardcoded
  // choice per field.
  //
  //  - all options loaded  -> antd filters the complete list client-side (`optionFilterProp`), and
  //    `handleSearch` short-circuits without a request. Instant, and it can never lie.
  //  - list still truncated -> antd's client-side filter is turned OFF and the server is the only
  //    filter. Leaving it on would first narrow the loaded page, so a value that exists but has not
  //    been fetched yet renders as "no results" -- indistinguishable from "not in the data".
  //
  // Keyword is the case that forces this: 7,041 values against a 100-row page on dev-1.
  const filterOption = hasAllOptionsLoaded ? undefined : false;

  return (
    <ControlWithLabel label={label} className={className}>
      <StyledSelect
        mode={isMultiple ? 'multiple' : undefined}
        showSearch={isSearchable}
        optionFilterProp="children"
        filterOption={filterOption}
        value={isMultiple ? value || [] : value}
        onChange={onChange}
        placeholder={placeholder ? placeholder : PLACEHOLDER_SELECT}
        size="small"
        maxTagCount={isMultiple ? 'responsive' : undefined}
        allowClear={allowClear}
        disabled={isDisabled}
        onSearch={handleSearch}
        dropdownRender={(menu) => (
          <>
            {menu}
            {isLoading && (
              <div style={{ padding: '8px', textAlign: 'center' }}>
                <Spin size="small" />
              </div>
            )}
          </>
        )}
        onPopupScroll={handlePopupScroll}
        onClear={handleSearch}>
        {options.map(({ label, value }) => {
          return (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          );
        })}
      </StyledSelect>
    </ControlWithLabel>
  );
};

const StyledSelect = styled(Select)`
  &&&.ant-select-open,
  &&&:not(.ant-select-disabled):hover {
    .ant-select-selector {
      border-color: #21b4bd;
      box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.2);
    }
  }
`;

DropDown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array
  ]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  isSearchable: PropTypes.bool,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  allowClear: PropTypes.bool,
  handleSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  handlePopupScroll: PropTypes.func,
  isMultiple: PropTypes.bool,
  hasAllOptionsLoaded: PropTypes.bool
};

DropDown.defaultProps = {
  options: [],
  isSearchable: false,
  isDisabled: false,
  allowClear: false,
  isMultiple: false,
  // Default to the safe side: assume the list may be partial, so search goes to the server rather
  // than silently filtering whatever happens to be loaded.
  hasAllOptionsLoaded: false
};

export default DropDown;
